import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { ENTITY_LABELS, useAlertRulePreview } from '../../../hooks/useAlertRulePreview';
import { AlertRuleFormData } from '../../../types/Alerts/AlertRuleFormState';
import HostTarget from '../../../types/host/HostTarget';
import InstanceTarget from '../../../types/instance/InstanceTarget';

const SEVERITY_META: Record<number, { label: string; color: string }> = {
    0: { label: 'OK',       color: '#16a34a' }, // green — no alert
    1: { label: 'Critical', color: '#dc2626' }, // red
    2: { label: 'Warning',  color: '#f59e0b' }, // amber
    3: { label: 'Info',     color: '#2563eb' }, // blue (vivid, distinct from green)
};

const formatValue = (value: number | undefined, units: string): string => {
    if (value === undefined || value === null || Number.isNaN(value)) {
        return '-';
    }

    const formatted = Number.isInteger(value)
        ? value.toLocaleString()
        : value.toLocaleString(undefined, { maximumFractionDigits: 2 });

    return units ? `${formatted}${units}` : formatted;
};

function AlertRulePreview(props: {
    formData: AlertRuleFormData,
    instances: InstanceTarget[],
    hosts: HostTarget[]
}) {
    const {
        entityType,
        matchedEntities,
        selectedEntityId,
        selectedEntity,
        setSelectedEntityId,
        series,
        loading,
        error,
        emptyReason,
        previewFrom,
        previewTo
    } = useAlertRulePreview(props.formData, props.instances, props.hosts);

    const yAxisPlotLines = useMemo(() => {
        return props.formData.thresholdarray
            .filter(threshold => !Number.isNaN(threshold.threshold))
            .map(threshold => {
                const meta = SEVERITY_META[threshold.severity] ?? SEVERITY_META[1];
                return {
                    value: threshold.threshold,
                    color: meta.color,
                    width: 1,
                    dashStyle: 'Dash',
                    zIndex: 5,
                    label: {
                        text: `${meta.label} ${formatValue(threshold.threshold, props.formData.units)}`,
                        align: 'right',
                        x: -6,
                        style: {
                            color: meta.color,
                            fontSize: '11px',
                            fontWeight: 'bold'
                        }
                    }
                };
            });
    }, [props.formData.thresholdarray, props.formData.units]);

    // Worst (lowest-numbered) severity that's currently triggered for an entity.
    const worstSeverity = (entity: typeof matchedEntities[number]): number => {
        if ((entity.severityCounts?.critical ?? 0) > 0) return 1;
        if ((entity.severityCounts?.warning ?? 0) > 0) return 2;
        if ((entity.severityCounts?.info ?? 0) > 0) return 3;
        return 0;
    };

    const chartOptions = useMemo(() => ({
        chart: {
            type: 'line',
            height: 260,
            backgroundColor: 'transparent',
            style: {
                fontFamily: 'system-ui, Arial, sans-serif',
                letterSpacing: 'normal'
            }
        },
        title: { text: '' },
        credits: { enabled: false },
        exporting: { enabled: false },
        accessibility: { enabled: false },
        legend: { enabled: false },
        time: {
            useUTC: false
        },
        xAxis: {
            type: 'datetime',
            lineColor: '#CCCCCC',
            tickColor: '#CCCCCC',
            labels: {
                style: {
                    color: '#666666',
                    fontSize: '11px',
                    letterSpacing: 'normal'
                }
            }
        },
        yAxis: {
            title: { text: props.formData.units || '' },
            min: 0,
            plotLines: yAxisPlotLines,
            labels: {
                style: {
                    color: '#666666',
                    fontSize: '11px',
                    letterSpacing: 'normal'
                }
            }
        },
        plotOptions: {
            series: {
                animation: false,
                marker: { enabled: false },
                turboThreshold: 0
            }
        },
        tooltip: {
            shared: true,
            valueSuffix: props.formData.units || '',
            valueDecimals: 2,
            xDateFormat: '%e %b %H:%M'
        },
        series: [{
            type: 'line',
            name: selectedEntity?.name ?? 'Preview',
            color: '#2f80ed',
            data: series.map(point => [point.x, point.y])
        }]
    }), [props.formData.units, selectedEntity?.name, series, yAxisPlotLines]);

    const entityLabel = ENTITY_LABELS[entityType];
    const getSeverityCount = (entity: typeof matchedEntities[number], key: 'info' | 'warning' | 'critical') =>
        entity.severityCounts?.[key] ?? 0;

    return (
        <div className="col-12 mb-3">
            <div className="row g-3 align-items-stretch">
                <div className="col-12 col-xl-7">
                    <div className="card h-100 mb-0">
                        <div className="card-header d-flex justify-content-between align-items-start">
                            <div>
                                <i className="fal fa-chart-line fa-fw mr-10" aria-hidden="true"/>
                                Metric preview
                                <small className="text-muted">
                                    {' '}— Last 24 hours, {previewFrom.format('DD MMM HH:mm')} to {previewTo.format('DD MMM HH:mm')}
                                </small>
                            </div>
                            {loading && (
                                <span className="text-muted small">
                                    <i className="fal fa-spinner fa-spin fa-fw" aria-hidden="true"/> Loading
                                </span>
                            )}
                        </div>

                        <div className="card-body">
                            {error && <div className="alert alert-warning py-2 mb-2">{error}</div>}
                            {emptyReason && !error && <div className="text-muted py-5 text-center">{emptyReason}</div>}
                            {!emptyReason && !error && (
                                <HighchartsReact constructorType="chart" highcharts={Highcharts} options={chartOptions}/>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-12 col-xl-5">
                    <div className="card h-100 mb-0">
                        <div className="card-header">
                            <i className="fal fa-list fa-fw mr-10" aria-hidden="true"/>
                            Matching {entityLabel}
                            <small className="text-muted">
                                {' '}— {matchedEntities.length} {entityLabel} match this rule scope.
                            </small>
                        </div>

                        <div className="card-body">
                            <div className="table-responsive" style={{ maxHeight: 292 }}>
                                <table className="table table-sm table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>Name</th>
                                            <th>Type</th>
                                            <th className="text-end">Latest</th>
                                            <th className="text-end">Max</th>
                                            <th className="text-end">Info</th>
                                            <th className="text-end">Warning</th>
                                            <th className="text-end">Critical</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {matchedEntities.map(entity => {
                                            const sev = worstSeverity(entity);
                                            const meta = SEVERITY_META[sev];
                                            return (
                                            <tr
                                                key={entity.id}
                                                role="button"
                                                className={entity.id === selectedEntityId ? 'table-active' : ''}
                                                onClick={() => setSelectedEntityId(entity.id)}
                                            >
                                                <td className="text-center" style={{ width: 24 }}>
                                                    <i className="fal fa-bell fa-fw"
                                                       style={{ color: meta.color }}
                                                       title={meta.label}
                                                       aria-label={meta.label}/>
                                                </td>
                                                <td className="text-nowrap">{entity.name}</td>
                                                <td className="text-nowrap">{entity.type}</td>
                                                <td className="text-end text-nowrap">{formatValue(entity.lastValue, props.formData.units)}</td>
                                                <td className="text-end text-nowrap">{formatValue(entity.maxValue, props.formData.units)}</td>
                                                <td className="text-end text-nowrap">{getSeverityCount(entity, 'info').toLocaleString()}</td>
                                                <td className="text-end text-nowrap">{getSeverityCount(entity, 'warning').toLocaleString()}</td>
                                                <td className="text-end text-nowrap">{getSeverityCount(entity, 'critical').toLocaleString()}</td>
                                            </tr>
                                            );
                                        })}
                                        {matchedEntities.length === 0 && (
                                            <tr>
                                                <td colSpan={8} className="text-muted text-center py-4">No matching {entityLabel}.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AlertRulePreview;
